import type { SectionType } from "@baze/schema";
import type { SectionProps } from "../context";
import { messageVars, type RenderContext } from "../context";
import { imageSrc, srcSet, telHref, waLink } from "../helpers";
import { Icon, type IconName } from "../icons";
import type { NavLink } from "../nav";
import { Container, SectionShell, SiteLink, cx, resolveCta } from "../ui";

/**
 * Home link on a one-page site. An empty fragment always means "top of the document" (HTML spec);
 * `#top` would not do, since the sticky navbar itself carries the `top` anchor and is always in view.
 */
export const TOP_HREF = "#";

function Brand({ ctx, href, className, showTitle }: { ctx: RenderContext; href: string; className?: string; showTitle: boolean }) {
  const logo = ctx.site.logo;
  const src = logo ? imageSrc(logo, 480) : null;
  // No logo: the name is the only mark, so it stays even when the owner hid the title.
  const title = showTitle || !src;
  return (
    <SiteLink ctx={ctx} href={href} className={cx("flex min-h-11 items-center gap-3", title ? "min-w-0" : "shrink-0", className)} ariaLabel={`${ctx.site.name} – beranda`}>
      {logo && src ? (
        <img
          src={src}
          srcSet={srcSet(logo)}
          sizes="200px"
          alt={logo.alt && logo.alt !== ctx.site.name ? logo.alt : title ? "" : ctx.site.name}
          width={logo.width}
          height={logo.height}
          loading="eager"
          decoding="async"
          className="h-9 w-auto max-w-[12rem] shrink-0 object-contain @3xl:h-10"
        />
      ) : null}
      {title ? <span className="baze-heading line-clamp-2 min-w-0 py-1 text-lg leading-tight [overflow-wrap:anywhere] @sm:text-xl @3xl:truncate @3xl:text-[1.375rem]">{ctx.site.name}</span> : null}
    </SiteLink>
  );
}

function NavLinks({ ctx, links, className, linkClassName }: { ctx: RenderContext; links: NavLink[]; className?: string; linkClassName?: string }) {
  if (links.length === 0) return null;
  return (
    <ul className={className}>
      {links.map((l) => (
        <li key={l.href}>
          <SiteLink ctx={ctx} href={l.href} className={cx(linkClassName, l.current && "text-site-text")}>
            <span aria-current={l.current ? "page" : undefined}>{l.label}</span>
          </SiteLink>
        </li>
      ))}
    </ul>
  );
}

type NavCta = { href: string; whatsapp: boolean; label: string; icon: IconName };

/**
 * The navbar button's target: the owner's choice (`ctaKind`), else automatic: the site's WhatsApp, the
 * contact section, or the phone. Unusable choices are dropped live and shown as a placeholder in the editor.
 */
function navCta(ctx: RenderContext, c: SectionProps<"navbar">["node"]["content"], label: string): NavCta | null {
  if (c.ctaKind) {
    const r = resolveCta(ctx, { label, kind: c.ctaKind, href: c.ctaHref, message: c.ctaMessage });
    if (r.href) return { href: r.href, whatsapp: r.whatsapp, label, icon: r.icon ?? "arrow-up-right" };
    return ctx.editor ? { href: "#", whatsapp: false, label, icon: c.ctaKind === "whatsapp" ? "whatsapp" : "arrow-up-right" } : null;
  }
  const wa = waLink(ctx.site.contact.whatsapp, ctx.site.whatsapp?.defaultMessage, messageVars(ctx));
  if (wa) return { href: wa, whatsapp: true, label, icon: "whatsapp" as const };
  if (ctx.anchors.has("kontak")) return { href: "#kontak", whatsapp: false, label, icon: "mail" as const };
  const tel = telHref(ctx.site.contact.phone);
  return tel ? { href: tel, whatsapp: false, label, icon: "phone" as const } : null;
}

function MobileMenu({ ctx, links, cta, floating }: { ctx: RenderContext; links: NavLink[]; cta: NavCta | null; floating?: boolean }) {
  if (links.length === 0) return null;
  return (
    <details className="group @3xl:hidden">
      <summary className="grid size-11 cursor-pointer list-none place-items-center rounded-full border border-site-border text-site-text transition hover:bg-site-text/5 [&::-webkit-details-marker]:hidden">
        <span className="sr-only">Buka menu</span>
        <Icon name="menu" className="size-5 group-open:hidden" />
        <Icon name="close" className="hidden size-5 group-open:block" />
      </summary>
      {/* hidden while closed: Chrome lays out closed <details> content in a contained box, where this
          panel collapses to the button's width and its text widens the page (horizontal scroll). */}
      <div className={cx("absolute inset-x-0 top-full hidden border-site-border bg-site-bg shadow-site-md group-open:block", floating ? "mt-2 rounded-site-lg border" : "border-b")}>
        <nav aria-label="Navigasi seluler" className="px-5 pt-2 pb-5">
          <NavLinks
            ctx={ctx}
            links={links}
            className="divide-y divide-site-border"
            linkClassName="flex min-h-12 items-center justify-between text-base font-medium text-site-text"
          />
          {cta ? (
            <SiteLink
              ctx={ctx}
              href={cta.href}
              whatsapp={cta.whatsapp}
              className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-site-button bg-site-primary px-5 font-semibold text-site-on-primary"
            >
              {cta.whatsapp ? <Icon name="whatsapp" className="size-[1.125rem]" /> : null}
              {cta.label}
            </SiteLink>
          ) : null}
        </nav>
      </div>
    </details>
  );
}

/**
 * The navbar call to action. With `compact` (a mobile menu is shown) it collapses below @3xl to an icon
 * button (WhatsApp, contact, or phone): the menu repeats the full label, and a wide pill would squeeze
 * the site name.
 */
function CtaPill({ ctx, cta, compact, className }: { ctx: RenderContext; cta: NavCta; compact?: boolean; className?: string }) {
  return (
    <SiteLink
      ctx={ctx}
      href={cta.href}
      whatsapp={cta.whatsapp}
      fieldPath="content.ctaLabel"
      ariaLabel={compact ? cta.label : undefined}
      className={cx(
        "inline-flex min-h-11 shrink-0 items-center gap-2 rounded-site-button bg-site-primary text-sm font-semibold whitespace-nowrap text-site-on-primary shadow-site-sm transition hover:shadow-site-md @3xl:px-5",
        compact ? "size-11 justify-center p-0 @3xl:size-auto" : "px-4",
        className,
      )}
    >
      {compact ? (
        <Icon name={cta.icon} className={cx("size-5 @3xl:size-4", !cta.whatsapp && "@3xl:hidden")} />
      ) : cta.whatsapp ? (
        <Icon name="whatsapp" className="size-4" />
      ) : null}
      <span className={compact ? "hidden @3xl:inline" : undefined}>{cta.label}</span>
    </SiteLink>
  );
}

/** Thin reading-progress bar along the bottom edge (scroll-driven CSS; hidden where unsupported). */
const Progress = () => <span aria-hidden="true" className="baze-progress pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-site-primary" />;

export function Navbar({ node, ctx }: SectionProps<"navbar">) {
  const c = node.content;
  const showTitle = c.showTitle !== false;
  const cta = c.showCta === false ? null : navCta(ctx, c, c.ctaLabel?.trim() || "Hubungi kami");
  const home = ctx.homeHref;
  const links = ctx.nav;
  const desktopLink = "inline-flex min-h-11 items-center px-3 text-[0.9375rem] font-medium text-site-muted transition-colors hover:text-site-text";

  if (node.variant === "floating") {
    return (
      <SectionShell ctx={ctx} node={node} as="header" padded={false} className="baze-navbar sticky top-(--baze-sticky-top,0px) z-40 bg-transparent! px-3 pt-3 [animation:none]! @3xl:px-6 @3xl:pt-4">
        <div className="relative mx-auto flex h-16 max-w-6xl items-center gap-3 rounded-full border border-site-border bg-site-bg/85 pr-2 pl-5 shadow-site-md backdrop-blur-md @3xl:gap-6 @3xl:pr-2.5 @3xl:pl-7">
          <Brand ctx={ctx} href={home} showTitle={showTitle} />
          {links.length > 0 ? (
            <nav aria-label="Navigasi utama" className="ml-auto hidden @3xl:block">
              <NavLinks ctx={ctx} links={links} className="flex items-center gap-0.5" linkClassName={cx(desktopLink, "rounded-full hover:bg-site-text/5")} />
            </nav>
          ) : null}
          <div className={cx("flex items-center gap-2", links.length === 0 ? "ml-auto" : "ml-auto @3xl:ml-0")}>
            {cta ? <CtaPill ctx={ctx} cta={cta} compact={links.length > 0} /> : null}
            <MobileMenu ctx={ctx} links={links} cta={cta} floating />
          </div>
        </div>
      </SectionShell>
    );
  }

  if (node.variant === "centered") {
    return (
      <SectionShell ctx={ctx} node={node} as="header" padded={false} className="baze-navbar sticky top-(--baze-sticky-top,0px) z-40 border-b border-site-border bg-site-bg">
        <Container className="grid h-16 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 @3xl:h-20 @3xl:grid-cols-[1fr_auto_1fr]">
          <div className="flex items-center">
            <MobileMenu ctx={ctx} links={links} cta={cta} />
          </div>
          <Brand ctx={ctx} href={home} showTitle={showTitle} className="justify-center text-center" />
          <div className="flex items-center justify-end">{cta ? <CtaPill ctx={ctx} cta={cta} compact={links.length > 0} /> : null}</div>
        </Container>
        {links.length > 0 ? (
          <nav aria-label="Navigasi utama" className="hidden border-t border-site-border @3xl:block">
            <Container>
              <NavLinks ctx={ctx} links={links} className="flex h-12 items-center justify-center gap-2" linkClassName={desktopLink} />
            </Container>
          </nav>
        ) : null}
        <Progress />
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node} as="header" padded={false} className="baze-navbar sticky top-(--baze-sticky-top,0px) z-40 border-b border-site-border bg-site-bg">
      <Container className="flex h-16 items-center gap-3 @3xl:h-20 @3xl:gap-6">
        <Brand ctx={ctx} href={home} showTitle={showTitle} />
        {links.length > 0 ? (
          <nav aria-label="Navigasi utama" className="ml-auto hidden @3xl:block">
            <NavLinks ctx={ctx} links={links} className="flex items-center gap-1" linkClassName={desktopLink} />
          </nav>
        ) : null}
        <div className={cx("flex items-center gap-2", links.length === 0 ? "ml-auto" : "ml-auto @3xl:ml-0")}>
          {cta ? <CtaPill ctx={ctx} cta={cta} compact={links.length > 0} /> : null}
          <MobileMenu ctx={ctx} links={links} cta={cta} />
        </div>
      </Container>
      <Progress />
    </SectionShell>
  );
}

const BAR_ICONS: Partial<Record<SectionType, IconName>> = {
  hero: "home",
  lessons: "book",
  faq: "help",
  contact: "phone",
  cta: "phone",
  location: "map-pin",
  "opening-hours": "clock",
  events: "calendar",
  countdown: "calendar",
  services: "sparkle",
  menu: "shopping-bag",
  products: "shopping-bag",
  picks: "shopping-bag",
  pricing: "shopping-bag",
  gallery: "heart",
  testimonials: "quote",
  "blog-latest": "book",
  links: "link",
};

/** Icon for a nav link: the type of the section it points to (or the first content section of its page). */
function barIcon(ctx: RenderContext, href: string): IconName {
  if (href.startsWith("#") || href.startsWith("/#")) {
    // "/#anchor": a home-page section, linked from a page kept out of the menu.
    const page = href.startsWith("/") ? ctx.definition.pages.find((p) => p.slug === "/") : ctx.page;
    const s = page?.sections.find((x) => `#${x.anchor}` === href.replace(/^\//, ""));
    return (s && BAR_ICONS[s.type]) || "arrow-up-right";
  }
  if (href === "/") return "home";
  const page = ctx.definition.pages.find((p) => p.slug === href);
  const first = page?.sections.find((x) => x.type !== "navbar" && x.type !== "hero" && x.type !== "footer");
  return (first && BAR_ICONS[first.type]) || "arrow-up-right";
}

/**
 * App-style bottom navigation on phones (navbar `mobileBar`): Beranda plus the first four nav links.
 * Rendered after the footer as a sticky bar, so it stays pinned inside a device frame too.
 */
export function MobileBar({ ctx }: { ctx: RenderContext }) {
  const multi = ctx.definition.pages.length > 1;
  const home = { label: "Beranda", href: ctx.homeHref, current: multi ? ctx.page.slug === "/" : undefined };
  const links = [home, ...ctx.nav.filter((l) => l.href !== "/")].slice(0, 5);
  if (links.length < 2) return null;
  return (
    <nav aria-label="Navigasi bawah" className="baze-mobilebar sticky bottom-0 z-40 border-t border-site-border bg-site-bg/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md @3xl:hidden">
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${links.length}, minmax(0, 1fr))` }}>
        {links.map((l, i) => (
          <li key={l.href}>
            <SiteLink ctx={ctx} href={l.href} className={cx("flex min-h-16 flex-col items-center justify-center gap-1 px-1 text-[0.6875rem] font-medium", l.current ? "text-site-primary" : "text-site-muted")}>
              <Icon name={i === 0 ? "home" : barIcon(ctx, l.href)} className="size-5" />
              <span className="max-w-full truncate" aria-current={l.current ? "page" : undefined}>
                {l.label}
              </span>
            </SiteLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
